import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import VideoPlayer from './video-player';
import { fetchVideoDetails } from '../../api/fetch-video-details';
import { TNullable } from '../../common/types';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

interface VideoPlayerDialogProps {
  docId: TNullable<string>;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function VideoPlayerDialog(props: VideoPlayerDialogProps) {
  const { t } = useTranslation();
  const [videoUrl, setVideoUrl] = useState<TNullable<string>>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let didCancel = false;

    async function loadVideo() {
      if (!props.open || !props.docId) {
        return;
      }

      setIsLoading(true);
      setVideoUrl(null);
      const result = await fetchVideoDetails(props.docId);

      if (!didCancel) {
        setVideoUrl(result?.video_url ?? null);
        setIsLoading(false);
      }
    }

    void loadVideo();

    return () => {
      didCancel = true;
    };
  }, [props.docId, props.open]);

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-5xl overflow-hidden rounded-lg p-0">
        <DialogHeader className="border-b border-slate-200 px-4 py-3 pr-12 sm:px-5">
          <DialogTitle className="text-base leading-tight text-slate-950">
            {props.title}
          </DialogTitle>
          <DialogDescription className="sr-only">{props.title}</DialogDescription>
        </DialogHeader>
        <div className="bg-slate-950 p-2 sm:p-4">
          {isLoading && (
            <div className="flex min-h-[220px] items-center justify-center gap-3 rounded-lg bg-black text-sm font-medium text-white/80 sm:min-h-[360px]">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('video.loading')}
            </div>
          )}
          {!isLoading && videoUrl && <VideoPlayer url={videoUrl} title={props.title} />}
          {!isLoading && !videoUrl && (
            <div className="flex min-h-[220px] items-center justify-center rounded-lg bg-black px-6 text-center text-sm font-medium text-white/80 sm:min-h-[360px]">
              {t('video.not_found')}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default VideoPlayerDialog;
